import { css, Interpolation, RuleSet } from 'styled-components';
import type { Styles } from 'styled-components/dist/types';

import {
    purpurBorderVariables,
    purpurBreakpoints,
    purpurColorVariables,
    purpurMotionVariables,
    purpurShadowVariables,
    purpurSpacingVariables,
    purpurTypographyVariables,
} from './purpurTokens';

type RemovePrefixAndLowerCase<
    TPrefix extends string,
    TValues extends string,
> = TValues extends `${TPrefix}${infer TFirst}${infer TRest}` ? `${Lowercase<TFirst>}${TRest}` : never;

type RemovePrefix<
    TPrefix extends string,
    TValues extends string,
> = TValues extends `${TPrefix}${infer TFirst}${infer TRest}` ? `${TFirst}${TRest}` : never;

type ObjectValues<TObject> = TObject[keyof TObject];

const capitalizeName = <TPrefix extends string, TValues extends string>(
    name: string,
): RemovePrefix<TPrefix, TValues> => {
    return `${name[0].toUpperCase()}${name.slice(1)}` as RemovePrefix<TPrefix, TValues>;
};

type Breakpoints = RemovePrefixAndLowerCase<'purpurBreakpoint', keyof typeof purpurBreakpoints>;

type PurpurMedia = (
    name: Breakpoints,
) => <Props extends object>(styles: Styles<Props>, ...interpolations: Interpolation<Props>[]) => RuleSet<Props>;
/**
 * @link https://purpur.telia.io/?path=/docs/tokens-breakpoint--tokens
 * @name purpurMedia
# Breakpoint
| Name | Value |
|--------|------|
| sm | 0px |
| md | 600px |
| lg | 1024px |
| xl | 1440px |
| relativeSm | 0em |
| relativeMd | 37.5em |
| relativeLg | 64em |
| relativeXl | 90em | */
export const purpurMedia: PurpurMedia = (name: Breakpoints) => {
    const breakpoint: keyof typeof purpurBreakpoints = `purpurBreakpoint${capitalizeName<'purpurBreakpoint', keyof typeof purpurBreakpoints>(name)}`;

    return ((...parameters: Parameters<typeof css>) => css`
        @media screen and (min-width: ${purpurBreakpoints[breakpoint]}) {
            ${css(...parameters)};
        }
    `) as <Props extends object>(styles: Styles<Props>, ...interpolations: Interpolation<Props>[]) => RuleSet<Props>;
};

/**
 * @link https://purpur.telia.io/?path=/docs/tokens-border--tokens
 * @name getPurpurBorder
# Border
| Name | Value |
|--------|------|
| radiusXs | 2px |
| radiusSm | 4px |
| radiusMd | 8px |
| radiusLg | 16px |
| radiusXl | 24px |
| radiusFull | 999px |
| widthXs | 1px |
| widthSm | 2px |
| widthMd | 3px |
| widthLg | 6px | */
export const getPurpurBorder = (
    name: RemovePrefixAndLowerCase<'purpurBorder', keyof typeof purpurBorderVariables>,
): ObjectValues<typeof purpurBorderVariables> => {
    const id = capitalizeName<'purpurBorder', keyof typeof purpurBorderVariables>(name);

    return purpurBorderVariables[`purpurBorder${id}`];
};

/** @link https://purpur.telia.io/?path=/story/tokens-color--base
 * @name getPurpurColor
# Color
| Name | Value |
|--------|------|
| purple50 | #faf0ff |
| purple100 | #f4e0ff |
| purple200 | #e4b6fb |
| purple300 | #d58df9 |
| purple400 | #c461f7 |
| purple500 | #b12df4 |
| purple550 | #990ae3 |
| purple600 | #8c07d0 |
| purple700 | #6d02a3 |
| purple800 | #4e0174 |
| purple900 | #29003e |
| purple950 | #1f002e |
| beige50 | #f9f1ec |
| beige100 | #ede1d8 |
| beige200 | #d7c3b5 |
| beige300 | #bba494 |
| beige400 | #a08877 |
| beige500 | #807268 |
| beige600 | #665e58 |
| beige700 | #4f4741 |
| beige800 | #3b332d |
| beige900 | #2d2824 |
| beige950 | #170f09 |
| teal50 | #edf5f5 |
| teal100 | #d5eae9 |
| teal200 | #a1cfcd |
| teal300 | #6eb5b2 |
| teal400 | #3a9b96 |
| teal500 | #00847e |
| teal600 | #006c67 |
| teal700 | #02534f |
| teal800 | #063c39 |
| teal900 | #032220 |
| teal950 | #011413 |
| gray50 | #f5f5fa |
| gray100 | #e5e5eb |
| gray150 | #d6d6dd |
| gray200 | #c6c6cd |
| gray300 | #aaaab0 |
| gray400 | #8d8d94 |
| gray500 | #757579 |
| gray600 | #5f5f63 |
| gray700 | #4a4a4d |
| gray800 | #353537 |
| gray850 | #2a2a2c |
| gray900 | #202022 |
| gray950 | #121212 |
| green50 | #e1f9ec |
| green100 | #c5efd9 |
| green200 | #79d9a7 |
| green300 | #3dbf7c |
| green400 | #1fa15e |
| green500 | #018842 |
| green600 | #017037 |
| green700 | #02562b |
| green800 | #003e1e |
| green900 | #002411 |
| green950 | #00150a |
| red50 | #fff0f5 |
| red100 | #ffdde8 |
| red200 | #ffafc9 |
| red300 | #ff80ab |
| red400 | #fb4885 |
| red500 | #e4175c |
| red600 | #c00543 |
| red700 | #980233 |
| red800 | #700025 |
| red900 | #440016 |
| red950 | #2c000e |
| orange50 | #fff2de |
| orange100 | #ffe1b3 |
| orange200 | #f9bb5b |
| orange300 | #f39200 |
| orange400 | #cc7a00 |
| orange500 | #a86602 |
| orange600 | #8b5301 |
| orange700 | #6a4102 |
| orange800 | #4c2f03 |
| orange900 | #2b1b03 |
| orange950 | #1a0f00 |
| blue50 | #e8f6ff |
| blue100 | #caeaff |
| blue200 | #91cdf4 |
| blue300 | #62b2e7 |
| blue400 | #3694d3 |
| blue500 | #0078cb |
| blue600 | #0063a6 |
| blue700 | #014d80 |
| blue800 | #03385b |
| blue900 | #002036 |
| blue950 | #011320 |
| transparentBlack50 | rgba(0, 0, 0, 0.05) |
| transparentBlack100 | rgba(0, 0, 0, 0.1) |
| transparentBlack150 | rgba(0, 0, 0, 0.16) |
| transparentBlack200 | rgba(0, 0, 0, 0.22) |
| transparentBlack300 | rgba(0, 0, 0, 0.33) |
| transparentBlack400 | rgba(0, 0, 0, 0.44) |
| transparentBlack500 | rgba(0, 0, 0, 0.54) |
| transparentBlack600 | rgba(0, 0, 0, 0.62) |
| transparentBlack700 | rgba(0, 0, 0, 0.71) |
| transparentBlack800 | rgba(0, 0, 0, 0.8) |
| transparentBlack850 | rgba(0, 0, 0, 0.84) |
| transparentBlack900 | rgba(0, 0, 0, 0.88) |
| transparentBlack950 | rgba(0, 0, 0, 0.93) |
| transparentWhite50 | rgba(255, 255, 255, 0.07) |
| transparentWhite100 | rgba(255, 255, 255, 0.11) |
| transparentWhite150 | rgba(255, 255, 255, 0.16) |
| transparentWhite200 | rgba(255, 255, 255, 0.21) |
| transparentWhite300 | rgba(255, 255, 255, 0.29) |
| transparentWhite400 | rgba(255, 255, 255, 0.37) |
| transparentWhite500 | rgba(255, 255, 255, 0.46) |
| transparentWhite600 | rgba(255, 255, 255, 0.55) |
| transparentWhite700 | rgba(255, 255, 255, 0.66) |
| transparentWhite800 | rgba(255, 255, 255, 0.79) |
| transparentWhite850 | rgba(255, 255, 255, 0.84) |
| transparentWhite900 | rgba(255, 255, 255, 0.89) |
| transparentWhite950 | rgba(255, 255, 255, 0.95) |
| functionalBlack | #000000 |
| functionalWhite | #ffffff |
| functionalTransparent | rgba(255, 255, 255, 0) |
| brandDeepPurple | #29003e |
| brandCorePurple | #990ae3 |
| brandLightPurple | #f4e0ff |
| brandDeepBeige | #4f4741 |
| brandBeige | #ede1d8 |
| brandLightBeige | #f9f1ec |
| brandOffBlack | #202022 |
| brandWhite | #ffffff |
| textDefault | rgba(0, 0, 0, 0.84) |
| textMedium | rgba(0, 0, 0, 0.62) |
| textWeak | rgba(0, 0, 0, 0.44) |
| textDefaultNegative | rgba(255, 255, 255, 0.95) |
| textMediumNegative | rgba(255, 255, 255, 0.66) |
| textWeakNegative | rgba(255, 255, 255, 0.37) |
| textInteractivePrimary | #6d02a3 |
| textInteractivePrimaryHover | #4e0174 |
| textInteractivePrimaryActive | #29003e |
| textInteractiveOnPrimary | #ffffff |
| textInteractiveOnPrimaryNegative | #4e0174 |
| textInteractivePrimaryNegative | #f4e0ff |
| textInteractivePrimaryHoverNegative | #faf0ff |
| textInteractivePrimaryActiveNegative | #ffffff |
| textInteractiveOnExpressive | #ffffff |
| textInteractiveOnExpressiveNegative | #ffffff |
| textInteractiveOnExpressiveHover | #ffffff |
| textInteractiveOnExpressiveHoverNegative | #29003e |
| textInteractiveOnExpressiveActive | #ffffff |
| textInteractiveOnExpressiveActiveNegative | #29003e |
| textInteractiveOnDestructive | #ffffff |
| textInteractiveSelected | #990ae3 |
| textInteractiveSelectedNegative | #d58df9 |
| textStatusDiscount | #990ae3 |
| textStatusErrorStrong | #980233 |
| textStatusErrorStrongNegative | #ff80ab |
| textStatusErrorMedium | #e4175c |
| textStatusErrorMediumNegative | #fb4885 |
| textStatusSuccessStrong | #02562b |
| textStatusSuccessStrongNegative | #3dbf7c |
| textStatusSuccessMedium | #018842 |
| textStatusSuccessMediumNegative | #1fa15e |
| textStatusWarningStrong | #6a4102 |
| textStatusWarningMedium | #a86602 |
| textStatusWarningMediumNegative | #f39200 |
| textStatusInfoStrong | #014d80 |
| textStatusInfoMedium | #0078cb |
| textStatusNeutral | rgba(0, 0, 0, 0.71) |
| textStatusSpecial | #4e0174 |
| textStatusAttention | #ffffff |
| textStatusBlackFriday | #ffffff |
| textToneOnTonePrimary | #f4e0ff |
| textToneOnToneSecondary | #29003e |
| textToneOnToneTertiary | #4f4741 |
| backgroundPrimary | #ffffff |
| backgroundPrimaryNegative | #1f002e |
| backgroundSecondary | #f5f5fa |
| backgroundSecondaryNegative | #29003e |
| backgroundOnImage | rgba(255, 255, 255, 0.84) |
| backgroundOnImageNegative | rgba(0, 0, 0, 0.71) |
| backgroundInteractiveAutoFill | #faf0ff |
| backgroundInteractivePrimary | #4e0174 |
| backgroundInteractivePrimaryHover | #6d02a3 |
| backgroundInteractivePrimaryActive | #8c07d0 |
| backgroundInteractivePrimaryNegative | #f4e0ff |
| backgroundInteractivePrimaryHoverNegative | #e4b6fb |
| backgroundInteractivePrimaryActiveNegative | #d58df9 |
| backgroundInteractiveExpressive | #990ae3 |
| backgroundInteractiveExpressiveHover | #6d02a3 |
| backgroundInteractiveExpressiveActive | #4e0174 |
| backgroundInteractiveExpressiveNegative | #990ae3 |
| backgroundInteractiveExpressiveHoverNegative | #c461f7 |
| backgroundInteractiveExpressiveActiveNegative | #d58df9 |
| backgroundInteractiveDisabled | rgba(0, 0, 0, 0.1) |
| backgroundInteractiveDisabledNegative | rgba(255, 255, 255, 0.11) |
| backgroundInteractiveTransparentHover | #f4e0ff |
| backgroundInteractiveTransparentHoverNegative | #4e0174 |
| backgroundInteractiveTransparentActive | #e4b6fb |
| backgroundInteractiveTransparentActiveNegative | #6d02a3 |
| backgroundInteractiveInactive | rgba(0, 0, 0, 0.05) |
| backgroundInteractiveInactiveNegative | rgba(255, 255, 255, 0.16) |
| backgroundInteractiveReadOnly | rgba(0, 0, 0, 0.05) |
| backgroundInteractiveReadOnlyNegative | rgba(255, 255, 255, 0.11) |
| backgroundInteractiveDestructive | #e4175c |
| backgroundInteractiveDestructiveHover | #c00543 |
| backgroundInteractiveDestructiveActive | #980233 |
| backgroundStatusInfoMedium | #caeaff |
| backgroundStatusInfoStrong | #3694d3 |
| backgroundStatusSuccessMedium | #c5efd9 |
| backgroundStatusSuccessStrong | #1fa15e |
| backgroundStatusSuccessStrongNegative | #3dbf7c |
| backgroundStatusErrorMedium | #ffdde8 |
| backgroundStatusErrorStrong | #fb4885 |
| backgroundStatusErrorStrongNegative | #ff80ab |
| backgroundStatusWarningMedium | #ffe1b3 |
| backgroundStatusWarningStrong | #cc7a00 |
| backgroundStatusWarningStrongNegative | #f39200 |
| backgroundStatusNeutral | #d6d6dd |
| backgroundStatusSpecial | #f4e0ff |
| backgroundStatusAttention | #6d02a3 |
| backgroundStatusBlackFriday | #202022 |
| backgroundToneOnTonePrimary | #29003e |
| backgroundToneOnToneSecondary | #f4e0ff |
| backgroundToneOnToneTertiary | #ede1d8 |
| backgroundToneOnToneQuaternary | #f9f1ec |
| borderMedium | rgba(0, 0, 0, 0.33) |
| borderMediumNegative | rgba(255, 255, 255, 0.29) |
| borderStrong | rgba(0, 0, 0, 0.54) |
| borderStrongNegative | rgba(255, 255, 255, 0.46) |
| borderWeak | rgba(0, 0, 0, 0.16) |
| borderWeakNegative | rgba(255, 255, 255, 0.16) |
| borderInteractiveFocus | #b12df4 |
| borderInteractivePrimary | #6d02a3 |
| borderInteractivePrimaryNegative | #f4e0ff |
| borderInteractivePrimaryHover | #4e0174 |
| borderInteractivePrimaryActive | #29003e |
| borderInteractivePrimaryHoverNegative | #faf0ff |
| borderInteractivePrimaryActiveNegative | #ffffff |
| borderInteractiveExpressive | #990ae3 |
| borderInteractiveSelected | #990ae3 |
| borderInteractiveSubtle | rgba(0, 0, 0, 0.44) |
| borderInteractiveSubtleNegative | rgba(255, 255, 255, 0.46) |
| borderInteractiveSubtleHover | #6d02a3 |
| borderInteractiveSubtleHoverNegative | #c461f7 |
| borderStatusError | #e4175c |
| borderStatusErrorNegative | #fb4885 |
| borderStatusWarning | #cc7a00 |
| borderStatusInfo | #0078cb |
| borderStatusSuccess | #018842 |
| borderStatusSuccessNegative | #1fa15e |
| overlayDefault | rgba(0, 0, 0, 0.33) |
| gradientAiBottom | #3694d3 |
| gradientAiBottomNegative | #62b2e7 |
| gradientAiTop | #b12df4 |
| gradientAiTopNegative | #d58df9 | */
export const getPurpurColor = (
    name: RemovePrefixAndLowerCase<'purpurColor', keyof typeof purpurColorVariables>,
): ObjectValues<typeof purpurColorVariables> => {
    const id = capitalizeName<'purpurColor', keyof typeof purpurColorVariables>(name);

    return purpurColorVariables[`purpurColor${id}`];
};

/** @link https://purpur.telia.io/?path=/docs/tokens-motion--tokens
 * @name getPurpurMotion
# Motion
| Name | Value |
|--------|------|
| duration100 | 100ms |
| duration150 | 150ms |
| duration200 | 200ms |
| duration300 | 300ms |
| duration400 | 400ms |
| duration500 | 500ms |
| duration1500 | 1500ms |
| duration2000 | 2000ms |
| easingEaseIn | cubic-bezier(0.35, 0.07, 0.88, 0.43) |
| easingEaseOut | cubic-bezier(0.33, 0.9, 0.75, 0.95) |
| easingEaseInOut | cubic-bezier(0.45, 0, 0.52, 0.98) | */
export const getPurpurMotion = (
    name: RemovePrefixAndLowerCase<'purpurMotion', keyof typeof purpurMotionVariables>,
): ObjectValues<typeof purpurMotionVariables> => {
    const id = capitalizeName<'purpurMotion', keyof typeof purpurMotionVariables>(name);

    return purpurMotionVariables[`purpurMotion${id}`];
};

/**
 * @link https://purpur.telia.io/?path=/story/tokens-shadow--shadow
 * @name getPurpurShadow
# Shadow
| Name | Value |
|--------|------|
| none | 0rem 0rem 0rem 0rem rgba(0, 0, 0, 0.1) |
| sm | 0rem 0.0625rem 0.25rem 0rem rgba(0, 0, 0, 0.1) |
| md | 0rem 0.25rem 0.75rem 0rem rgba(0, 0, 0, 0.1) |
| lg | 0rem 0.75rem 1.5rem 0rem rgba(0, 0, 0, 0.1) | */
export const getPurpurShadow = (
    name: RemovePrefixAndLowerCase<'purpurShadow', keyof typeof purpurShadowVariables>,
): ObjectValues<typeof purpurShadowVariables> => {
    const id = capitalizeName<'purpurShadow', keyof typeof purpurShadowVariables>(name);

    return purpurShadowVariables[`purpurShadow${id}`];
};

/**
 * @link https://purpur.telia.io/?path=/docs/tokens-spacing--base-tokens
 * @name getPurpurSpacing
# Spacing
| Name | Value | px |
|--------|------|------|
| 0 | 0rem | 0px |
| 10 | 0.0625rem | 1px |
| 25 | 0.125rem | 2px |
| 50 | 0.25rem | 4px |
| 100 | 0.5rem | 8px |
| 150 | 0.75rem | 12px |
| 200 | 1rem | 16px |
| 250 | 1.25rem | 20px |
| 300 | 1.5rem | 24px |
| 400 | 2rem | 32px |
| 600 | 3rem | 48px |
| 800 | 4rem | 64px |
| 1000 | 5rem | 80px |
| 1200 | 6rem | 96px |
| 1600 | 8rem | 128px |
| gutterSm | 1rem | 16px |
| gutterMd | 1.5rem | 24px |
| gutterLg | 1.5rem | 24px |
| gutterXl | 1.5rem | 24px |
| pagePaddingSm | 1rem | 16px |
| pagePaddingMd | 1.5rem | 24px |
| pagePaddingLg | 3rem | 48px |
| pagePaddingXl | 3rem | 48px | */
export const getPurpurSpacing = (
    name: RemovePrefixAndLowerCase<'purpurSpacing', keyof typeof purpurSpacingVariables>,
): ObjectValues<typeof purpurSpacingVariables> => {
    const id = capitalizeName<'purpurSpacing', keyof typeof purpurSpacingVariables>(name);

    return purpurSpacingVariables[`purpurSpacing${id}`];
};

/**
 * @link https://purpur.telia.io/?path=/docs/tokens-typography--tokens
 * @name getPurpurTypography
# Typography
| Name | Value | px |
|--------|------|------|
| familyDefault | TeliaSans, Helvetica, Arial, 'Lucida Grande', sans-serif |  |
| familyDisplay | TeliaSansHeading, Helvetica, Arial, 'Lucida Grande', sans-serif |  |
| weightNormal | 400 |  |
| weightMedium | 500 |  |
| weightBold | 700 |  |
| lineHeightTight | 100% |  |
| lineHeightSnug | 110% |  |
| lineHeightDefault | 125% |  |
| lineHeightLoose | 150% |  |
| lineHeightLooser | 175% |  |
| scale50 | 0.75rem | 12px |
| scale75 | 0.875rem | 14px |
| scale100 | 1rem | 16px |
| scale150 | 1.125rem | 18px |
| scale200 | 1.25rem | 20px |
| scale300 | 1.375rem | 22px |
| scale400 | 1.5rem | 24px |
| scale500 | 1.75rem | 28px |
| scale600 | 2rem | 32px |
| scale650 | 2.25rem | 36px |
| scale700 | 2.5rem | 40px |
| scale800 | 3rem | 48px |
| scale850 | 3.5rem | 56px |
| scale900 | 4rem | 64px |
| scale1000 | 5rem | 80px |
| scale1100 | 6rem | 96px | */
export const getPurpurTypography = (
    name: RemovePrefixAndLowerCase<'purpurTypography', keyof typeof purpurTypographyVariables>,
): ObjectValues<typeof purpurTypographyVariables> => {
    const id = capitalizeName<'purpurTypography', keyof typeof purpurTypographyVariables>(name);

    return purpurTypographyVariables[`purpurTypography${id}`];
};
